import React from "react";

// ── Avatar helpers ──────────────────────────────────────────────────────────
export const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/);
    return parts.length > 1
        ? (parts[0][0] + parts[1][0]).toUpperCase()
        : parts[0].slice(0, 2).toUpperCase();
};

export function Avatar({ name, size = 36 }) {
    return (
        <div
            className="icv2-avatar"
            style={{
                width: size,
                height: size,
                fontSize: Math.max(10, size * 0.3),
            }}
        >
            {getInitials(name)}
        </div>
    );
}

// ── Skills ───────────────────────────────────────────────────────────────────
export const parseSkills = (skills) => {
    if (!skills) return [];
    if (typeof skills === "string") return skills.split(",").map((s) => s.trim()).filter(Boolean);
    return skills;
};

export function SkillTags({ skills, secondarySkills, max = 3 }) {
    const primary = parseSkills(skills);
    const secondary = parseSkills(secondarySkills);
    const all = [...primary, ...secondary];
    if (!all.length) return null;
    const visible = all.slice(0, max);
    const extra = all.length - visible.length;
    return (
        <div className="icv2-skill-tags">
            {visible.map((s, i) => (
                <span key={i} className="icv2-skill-tag">{s}</span>
            ))}
            {extra > 0 && <span className="icv2-skill-tag icv2-skill-tag--more">+{extra} more</span>}
        </div>
    );
}

// ── Status / source metadata ─────────────────────────────────────────────────
export const STATUS_META = {
    invite_sent: { label: "Invite sent", bg: "#F3E8FF", color: "#7C3AED", border: "#DDD6FE" },
    opened: { label: "Opened", bg: "#EFF6FF", color: "#2563EB", border: "#BFDBFE" },
    applied: { label: "Applied", bg: "#F0FDF4", color: "#16A34A", border: "#BBF7D0" },
    bounced: { label: "Bounced", bg: "#FEF2F2", color: "#EF4444", border: "#FECACA" },
    processing: { label: "Processing…", bg: "#F9FAFB", color: "#6B7280", border: "#E4E9F0" },
    failed: { label: "Failed", bg: "#FEF2F2", color: "#EF4444", border: "#FECACA" },
};

export const SOURCE_META = {
    smart_search: "Smart search",
    csv_import: "CSV import",
    resume_upload: "Resume upload",
};

export function StatusPill({ status }) {
    const meta = STATUS_META[status] || STATUS_META.invite_sent;
    return (
        <span
            className="icv2-status-pill"
            style={{ background: meta.bg, color: meta.color, border: `1px solid ${meta.border}` }}
        >
            {meta.label}
        </span>
    );
}

export function SourcePill({ source }) {
    return <span className="icv2-source-pill">{SOURCE_META[source] || source || "N/A"}</span>;
}

// Invite status pill driven by the /hiring-statuses/?type=job_candidate_invite
// records — falls back to a slug-based palette when the API gives no colour.
const INVITE_STATUS_FALLBACK = {
    invited: { bg: "#F3E8FF", color: "#7C3AED", border: "#DDD6FE" },
    invite_sent: { bg: "#F3E8FF", color: "#7C3AED", border: "#DDD6FE" },
    applied: { bg: "#F0FDF4", color: "#16A34A", border: "#BBF7D0" },
    no_response: { bg: "#FEF2F2", color: "#EF4444", border: "#FECACA" },
};

export function InviteStatusPill({ label, slug, color }) {
    const key = (slug || label || "").toString().toLowerCase().replace(/\s+/g, "_");
    const fallback = INVITE_STATUS_FALLBACK[key] || STATUS_META.invite_sent;
    const text = color || fallback.color;
    return (
        <span
            className="icv2-status-pill"
            style={{ background: fallback.bg, color: text, border: `1px solid ${fallback.border}` }}
        >
            {label || "—"}
        </span>
    );
}

// Shared "Already applied" toggle used by the CSV / resume upload panels.
// Checked  → already_applied = true  (straight to job candidates)
// Unchecked → already_applied = false (invite + email → Invited tab)
export function AlreadyAppliedCheckbox({ checked, onChange, disabled }) {
    return (
        <label className={`icv2-already-applied${disabled ? " icv2-already-applied--disabled" : ""}`}>
            <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={(e) => onChange(e.target.checked)}
            />
            <span className="icv2-already-applied-text">
                <strong>Already applied</strong>
                <span>Add these candidates straight to the job instead of sending an invite email.</span>
            </span>
        </label>
    );
}

// ── Date formatting ──────────────────────────────────────────────────────────
export const formatInvitedDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    const isSameDay = (a, b) => a.toDateString() === b.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (isSameDay(d, now)) {
        const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
        return `Today, ${time}`;
    }
    if (isSameDay(d, yesterday)) return "Yesterday";
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

// ── File helpers (CSV / resume upload panels) ────────────────────────────────
export const MAX_FILES = 20;
export const ALLOWED_EXTENSIONS = [".pdf", ".docx"];
export const ALLOWED_MIME_TYPES = [
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

export const filterValidResumeFiles = (files) =>
    files.filter((file) => {
        const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
        return ALLOWED_MIME_TYPES.includes(file.type) || ALLOWED_EXTENSIONS.includes(ext);
    });

export function FileTypeIcon({ name }) {
    const ext = name.substring(name.lastIndexOf(".")).toLowerCase();
    const isPdf = ext === ".pdf";
    const isCsv = ext === ".csv";
    const bg = isPdf ? "#FFF0F4" : isCsv ? "#F0FFF4" : "#F0F4FF";
    const color = isPdf ? "#F75891" : isCsv ? "#16A34A" : "#4A6EE0";
    const border = isPdf ? "1px solid #F7589133" : isCsv ? "1px solid #16A34A33" : "1px solid #4A6EE033";
    return (
        <div className="icv2-file-icon" style={{ background: bg, color, border }}>
            {isPdf ? "PDF" : isCsv ? "CSV" : "DOC"}
        </div>
    );
}

// ── Minimal CSV parsing (no embedded-comma/quote support — matches the app's
// existing sample-CSV export format of plain, unquoted columns) ─────────────
export const parseCsvText = (text) => {
    const lines = text.split(/\r\n|\n|\r/).map((l) => l.trim()).filter((l) => l.length > 0);
    if (!lines.length) return { headers: [], rows: [] };
    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
    const rows = lines.slice(1).map((line) => {
        const cells = line.split(",").map((c) => c.trim());
        const row = {};
        headers.forEach((h, i) => { row[h] = cells[i] || ""; });
        return row;
    });
    return { headers, rows };
};
